import React, { useState, useEffect, useRef } from "react";
import { useImpressionJobStore } from "../../store/useImpressionJobStore";
import { useAppStore } from "../../store/useAppStore";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { JobAnalyticsSummary } from "./JobAnalyticsSummary";
import { JobAnalyticsCharts } from "./JobAnalyticsCharts";
import { JobAnalyticsTable } from "./JobAnalyticsTable";
import { apiService } from "../../services/api";
import {
  ArrowLeft,
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { format, parseISO, differenceInSeconds } from "date-fns";

export const ImpressionJobDetail: React.FC = () => {
  const selectedJobId = useAppStore((state) => state.selectedJobId);
  const navigateToView = useAppNavigate();
  const {
    selectedJob,
    currentAnalytics,
    analyticsLoading,
    fetchJobAnalytics,
    selectJob,
  } = useImpressionJobStore();

  const [activeTab, setActiveTab] = useState<"overview" | "charts" | "data">(
    "overview",
  );
  const [downloading, setDownloading] = useState(false);
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedJobId) {
      fetchJobAnalytics(selectedJobId).catch(console.error);
    }

    return () => {
      selectJob(null);
    };
  }, [selectedJobId, fetchJobAnalytics, selectJob]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowFormatDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDownloadResults = async (format: "csv" | "zip") => {
    if (!selectedJobId) return;

    try {
      setDownloading(true);
      setShowFormatDropdown(false);

      const downloadInfo =
        await apiService.downloadImpressionJobAnalytics(selectedJobId);
      window.location.assign(downloadInfo.download_url);

      console.log(
        `Download completed for job ${selectedJobId} as ${format.toUpperCase()}`,
      );
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "processing":
        return <Clock className="w-6 h-6 text-yellow-600" />;
      case "completed":
        return <CheckCircle2 className="w-6 h-6 text-green-600" />;
      case "failed":
        return <XCircle className="w-6 h-6 text-red-600" />;
      default:
        return <AlertCircle className="w-6 h-6 text-gray-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "processing":
        return "Processing";
      case "completed":
        return "Completed";
      case "failed":
        return "Failed";
      default:
        return "Unknown";
    }
  };

  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return "-";
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      const startFormatted = format(start, "MMM d");
      const endFormatted = format(end, "MMM d, yyyy");

      if (
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear()
      ) {
        return `${startFormatted} - ${format(end, "d, yyyy")}`;
      }
      return `${startFormatted} - ${endFormatted}`;
    } catch {
      return "-";
    }
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    if (!endTime) return "-";
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const seconds = differenceInSeconds(end, start);

      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    } catch {
      return "-";
    }
  };

  if (analyticsLoading && !selectedJob) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!selectedJob) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Job not found</p>
        <button
          onClick={() => navigateToView("impression-jobs")}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Jobs
        </button>
      </div>
    );
  }

  const totalImpressions = currentAnalytics?.analytics.reduce(
    (sum, bike) =>
      sum +
      bike.daily_impressions.reduce((s, day) => s + day.total_impressions, 0),
    0,
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateToView("impression-jobs")}
            className="p-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Impression Job Details
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Campaign: {selectedJob.campaign_id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedJob.status === "completed" && (
            <div className="relative" ref={dropdownRef}>
              <div className="inline-flex rounded-lg shadow-sm">
                {/* Main download button (ZIP artifact) */}
                <button
                  onClick={() => handleDownloadResults("zip")}
                  disabled={downloading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-l-lg"
                >
                  <Download
                    className={`w-4 h-4 ${downloading ? "animate-bounce" : ""}`}
                  />
                  {downloading ? "Downloading..." : "Download ZIP"}
                </button>

                {/* Dropdown toggle */}
                <button
                  onClick={() => setShowFormatDropdown(!showFormatDropdown)}
                  disabled={downloading}
                  className="inline-flex items-center px-2 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-l border-green-500 rounded-r-lg"
                  aria-label="Download format options"
                >
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${showFormatDropdown ? "rotate-180" : ""}`}
                  />
                </button>
              </div>

              {/* Dropdown menu */}
              {showFormatDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handleDownloadResults("zip")}
                      disabled={downloading}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download ZIP
                    </button>
                    {/* <button
                      onClick={() => handleDownloadResults("csv")}
                      disabled={downloading}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export analytics CSV
                    </button> */}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              {getStatusIcon(selectedJob.status)}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">Status</div>
              <div className="text-lg font-semibold text-gray-900">
                {getStatusText(selectedJob.status)}
              </div>
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-500">City</div>
            <div className="text-lg font-semibold text-gray-900 capitalize">
              {selectedJob.city}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-500">
              Analyzed Period
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {formatDateRange(
                selectedJob.analyzed_start_date,
                selectedJob.analyzed_end_date,
              )}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-500">
              Processing Duration
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {formatDuration(selectedJob.started_at, selectedJob.completed_at)}
            </div>
          </div>
        </div>

        {selectedJob.error_message && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>Error:</strong> {selectedJob.error_message}
            </p>
          </div>
        )}
      </div>

      {/* Content based on status */}
      {selectedJob.status === "processing" && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-lg text-gray-600">
            Processing impression data...
          </p>
          <p className="mt-2 text-sm text-gray-500">
            This may take a few minutes depending on the data size. Return to
            the jobs list and use list refresh to check progress.
          </p>
        </div>
      )}

      {selectedJob.status === "completed" && currentAnalytics && (
        <>
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab("overview")}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "overview"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab("charts")}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "charts"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Charts
                </button>
                <button
                  onClick={() => setActiveTab("data")}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "data"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Data Table
                </button>
              </nav>
            </div>

            <div className="p-6">
              {activeTab === "overview" && (
                <JobAnalyticsSummary
                  analytics={currentAnalytics.analytics}
                  totalImpressions={totalImpressions || 0}
                  rowCount={selectedJob.row_count || 0}
                />
              )}
              {activeTab === "charts" && (
                <JobAnalyticsCharts analytics={currentAnalytics.analytics} />
              )}
              {activeTab === "data" && (
                <JobAnalyticsTable analytics={currentAnalytics.analytics} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
